import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import courseService from "../../services/courseService";

export const fetchCourses = createAsyncThunk(
  "courses/fetchCourses",
  async (params, { rejectWithValue }) => {
    try {
      const response = await courseService.getAllCourses(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses"
      );
    }
  }
);

export const createCourse = createAsyncThunk(
  "courses/createCourse",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await courseService.createCourse(formData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create course"
      );
    }
  }
);

export const updateCourse = createAsyncThunk(
  "courses/updateCourse",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await courseService.updateCourse(id, formData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course"
      );
    }
  }
);

export const deleteCourse = createAsyncThunk(
  "courses/deleteCourse",
  async (id, { rejectWithValue }) => {
    try {
      const response = await courseService.deleteCourse(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete course"
      );
    }
  }
);

const courseSlice = createSlice({
  name: "courses",
  initialState: {
    courses: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.courses = responseData.courses || responseData || [];
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createCourse.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newCourse = responseData.course || responseData;
        if (newCourse) {
          state.courses.unshift(newCourse);
        }
        state.success = action.payload.message || "Course created successfully";
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      .addCase(updateCourse.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedCourse = responseData.course || responseData;
        const index = state.courses.findIndex((c) => c.id === updatedCourse.id);
        if (index !== -1) {
          state.courses[index] = updatedCourse;
        }
        state.success = action.payload.message || "Course updated successfully";
      })
      .addCase(updateCourse.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      .addCase(deleteCourse.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.courses = state.courses.filter((c) => c.id !== action.payload.id);
        state.success = action.payload.response.message || "Course deleted successfully";
      })
      .addCase(deleteCourse.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = courseSlice.actions;
export default courseSlice.reducer;
